import { inject, Injectable } from '@angular/core';
import { TODO } from '@b2b/types/core/utils';
import { HelpersService } from '../helpers/helpers.service';
import _ from 'lodash';

/**
 * RulesEngineService — Angular 18.
 *
 * Ported 1:1 from apps/b2b-ui/common/services/rulesEngine/rulesEngine.service.js
 * (legacy AngularJS name 'RulesEngineService'). Pure membership-lifecycle rule
 * logic: rule/category finders, restriction blocks, and term-group range options.
 *
 * Registered back into AngularJS under 'RulesEngineService' via downgradeInjectable
 * in app/upgrade/ng-downgraded-components.ts. `angular.copy` was replaced with
 * lodash `cloneDeep`.
 */
@Injectable({ providedIn: 'root' })
export class RulesEngineService {
  private helpersService = inject(HelpersService);

  model: TODO[] = [];
  groups: Record<string, TODO> = {
    IN_TERM: {
      rangeOptions: {
        backgroundColor: '#e4e4e4',
        restrictColor: '#b9ced5',
        activeColor: '#169bd5',
        startRange: 0,
        endRange: 12,
        defaultStartRange: 0,
        defaultEndRange: 12,
        startLabel: 'Membership Start (0 Months)',
        endLabel: 'Membership End (12 Months)',
        startLabelTemplate: 'Membership Start (%s)',
        endLabelTemplate: 'Membership End (%s)',
        anchoredStartLabel: ' ',
        anchoredEndLabel: 'Expiration',
        disableDrag: false,
      },
    },

    AFTER_TERM: {
      rangeOptions: {
        backgroundColor: '#e4e4e4',
        restrictColor: '#d1c3d1',
        activeColor: '#969',
        startRange: 0,
        endRange: 24,
        defaultStartRange: 0,
        defaultEndRange: 24,
        startLabel: 'Membership End (12 Months)',
        endLabel: '24 Months',
        startLabelTemplate: '%s',
        endLabelTemplate: '%s',
        anchoredStartLabel: ' ',
        anchoredEndLabel: 'Expiration',
        disableDrag: false,
      },
    },
  };

  ruleStartExpirationBasedOptions = [
    {
      label: 'From START of membership',
      value: false,
    },
    {
      label: 'From END of membership',
      value: true,
    },
  ];

  init(rules: TODO[]): void {
    this.model = rules;
  }

  nameToAction(name: string): string {
    return name.toUpperCase().replace(/\s/g, '_');
  }

  actionToName(label: string): string {
    const name: string[] = [];
    const parts = label.toLowerCase().split('_');

    for (let i = 0, ii = parts.length; i < ii; i++) {
      name.push(this.helpersService.cap(parts[i]));
    }

    return name.join(' ');
  }

  findRuleTemplate(id: TODO): TODO | false {
    let r: TODO | false = false;

    for (let i = 0, ii = this.model.length; i < ii; i++) {
      if (this.model[i].id === id) {
        r = {
          model: this.model[i],
          index: i,
        };
        break;
      }
    }

    return r;
  }

  findCategory(templateId: TODO, categoryId: TODO): TODO | false {
    let r: TODO | false = false;
    const found = this.findRuleTemplate(templateId);

    if (found) {
      for (let i = 0, ii = found.categories.length; i < ii; i++) {
        if (found.categories[i].id === categoryId) {
          r = {
            category: found.categories[i],
            index: i,
          };
          break;
        }
      }
    }

    return r;
  }

  findRuleById(templateId: TODO, categoryId: TODO, ruleId: TODO): TODO | false {
    let r: TODO | false = false;
    const found = this.findCategory(templateId, categoryId);

    if (found) {
      for (let i = 0, ii = found.rules.length; i < ii; i++) {
        if (found.rules[i].ruleId === ruleId) {
          r = {
            rule: found.rules[i],
            index: i,
          };
          break;
        }
      }
    }

    return r;
  }

  findRuleBySection(templateId: TODO, categoryId: TODO, section: TODO): TODO | false {
    let r: TODO | false = false;
    const found = this.findCategory(templateId, categoryId);

    if (found) {
      for (let i = 0, ii = found.rules.length; i < ii; i++) {
        if (found.rules[i].section.toLowerCase() === section.toLowerCase()) {
          r = {
            rule: found.rules[i],
            index: i,
          };
          break;
        }
      }
    }

    return r;
  }

  findCategoryRuleBySection(rules: TODO[], section: TODO, describe: boolean): TODO | false {
    let r: TODO | false = false;

    if (rules instanceof Array) {
      for (let i = 0, ii = rules.length; i < ii; i++) {
        if (rules[i].section.toLowerCase() === section.toLowerCase()) {
          r = {
            rule: rules[i],
            index: i,
          };
          break;
        }
      }

      if (describe) {
        r.description = this.getRuleDescription(r.rule);
      }
    }

    return r;
  }

  findSelectedRuleAction(rule: TODO, section: TODO): TODO | false {
    let r: TODO | false = false;

    if (section && rule.section.toLowerCase() !== section.toLowerCase()) {
      return r;
    }

    for (let i = 0, ii = rule.actions.length; i < ii; i++) {
      if (rule.actions[i].id === rule.selectedId) {
        r = {
          action: rule.actions[i],
          description: rule.name + ': ' + rule.actions[i].name,
          index: i,
        };
        break;
      }
    }

    return r;
  }

  getRuleStartExpirationBasedOptions() {
    return this.ruleStartExpirationBasedOptions.slice(0);
  }

  getOfferingActionRules(offering: TODO, section: TODO): TODO | false {
    let r: TODO | false = false;

    if (offering.actions instanceof Array) {
      for (let i = 0, ii = offering.actions.length; i < ii; i++) {
        if (offering.actions[i].action === section) {
          r = {
            ruleIds: offering.actions[i].ruleIds,
            index: i,
          };
          break;
        }
      }
    }

    return r;
  }

  getRuleDescription(rule: TODO) {
    let r = '';
    const section = false;
    const found = this.findSelectedRuleAction(rule, section);

    if (found) {
      r = rule.name + ': ' + found.action.name;
    }

    return r;
  }

  getRestrictionRules(blockLabel: TODO): TODO | false {
    let r: TODO | false = false;
    const restrictions: Record<string, TODO> = {
      NEW_MEMBERSHIPS: {
        order: 1,
        rules: {
          overlap: true,
          overlapGroup: true,
          acceptParams: false,
          group: 'IN_TERM',
          autoSelect: 'STANDARD_NEW',
        },
      },

      RENEW: {
        order: 2,
        rules: {
          overlap: true,
          overlapGroup: true,
          acceptParams: true,
          group: 'IN_TERM',
        },
      },

      UPGRADE: {
        order: 3,
        rules: {
          overlap: false,
          overlapGroup: true,
          acceptParams: true,
          group: 'IN_TERM',
        },
      },

      DOWNGRADE: {
        order: 4,
        rules: {
          overlap: false,
          overlapGroup: true,
          acceptParams: true,
          group: 'IN_TERM',
        },
      },

      CANCELLATION: {
        order: 5,
        rules: {
          overlap: false,
          overlapGroup: true,
          acceptParams: true,
          group: 'IN_TERM',
        },
      },

      REJOIN: {
        order: 2,
        rules: {
          overlap: true,
          overlapGroup: false,
          overlapCascade: true,
          acceptParams: true,
          group: 'AFTER_TERM',
        },
      },

      AFTER_TERM_RENEW: {
        order: 1,
        rules: {
          overlap: true,
          overlapGroup: false,
          acceptParams: true,
          overlapCascade: true,
          group: 'AFTER_TERM',
        },
      },

      groups: this.groups,
    };

    if (restrictions.hasOwnProperty(blockLabel)) {
      r = restrictions[blockLabel] as TODO;
      if (restrictions.groups.hasOwnProperty(r.rules.group)) {
        r.rangeOptions = _.cloneDeep(restrictions.groups[r.rules.group].rangeOptions);
      }
    }

    return r;
  }

  updateTermGroupsEndRange(group: string, endRange: number) {
    this.groups[group].endRange = endRange;
  }
}
